import { useEffect } from 'react'

import { useFilterStore, useMapStore } from '@monopolio-ui/state-library'

import {
  getGeometryForZone,
  getPolygon
} from '../../../filters-library/src/components/autocomplete/services/polygonServices'
import { ESearchLevel } from '@monopolio-ui/types-library'
import { ILocation } from '@monopolio-ui/state-library/stores/filterStore/types'
import { IGeometry } from '@monopolio-ui/state-library/stores/mapStore/types'
import { paintLookupPolygon, removeLookupPolygon } from '../utils/polygonUtils'
import { EGeometryType } from '../../../filters-library/src/components/autocomplete/interfaces/geoInterfaces'
import { polygon as polygonFunc } from '@turf/helpers'
import { featureCollection } from '@turf/helpers'
import { union } from '@turf/union'

export const usePolygons = (): void => {
  const { map, drawingTool , setZonePolygons } = useMapStore()

  const { latitude, longitude, searchLevel, searchAddress, locations } =
    useFilterStore()
  const loadZonePolygon = async ({
    zoneId,
    latitude,
    longitude,
    searchLevel
  }: ILocation): Promise<IGeometry | undefined> => {
    if (zoneId) {
      const { data: zone } = await getGeometryForZone(zoneId)
      return zone?.geometry
    } else if (latitude && longitude && searchLevel) {
      let level = searchLevel
      if ([ESearchLevel.PROPERTY, ESearchLevel.BlOCK].includes(searchLevel)) {
        level = ESearchLevel.NEIGHBORHOOD
      }
      const { data: polygon } = await getPolygon({
        center: {
          lat: latitude,
          lng: longitude
        },
        searchLevel: level,
        withGeometry: true
      })
      return polygon?.geometry
    }
    return undefined
  }

  const loadPolygons = async () => {
    if(drawingTool.isActive && map){
      removeLookupPolygon(map)
      return
    }
    if ((!locations || locations?.length === 0) && !searchAddress) {
      return
    }
    const locationsToPolygon =
      locations.length > 0
        ? locations
        : [
            {
              id: `${latitude}${longitude}`,
              latitude,
              longitude,
              searchLevel,
              searchAddress
            }
          ]
    const polygonPromises = locationsToPolygon.map((location) =>
      loadZonePolygon(location).catch((error) => {
        console.error(`Error loading polygon for location:`, location, error)
        return undefined
      })
    )
    const results = await Promise.allSettled(polygonPromises)
    const validPolygons = results
      .filter(
        (result): result is PromiseFulfilledResult<IGeometry | undefined> =>
          result.status === 'fulfilled'
      )
      .map((result) => result.value)
      .filter((polygon): polygon is IGeometry => polygon !== undefined)
    setZonePolygons(validPolygons)
    if (map) {
      removeLookupPolygon(map)
      if (validPolygons.length > 1) {
        const multiplesPolygons = validPolygons.map((polygon) =>
          polygonFunc(polygon.coordinates as number[][][])
        )
        const polygonUnions = union(featureCollection(multiplesPolygons))
        paintLookupPolygon({
          map,
          geometry: polygonUnions?.geometry as IGeometry,
          flyTo: true
        })
        return
      }
      const polygons = validPolygons.map((polygon) => polygon.coordinates)

      paintLookupPolygon({
        map,
        geometry: {
          type: EGeometryType.MultiPolygon,
          coordinates: polygons as GeoJSON.Position[] | GeoJSON.Position[][]
        },
        flyTo: true
      })
    }
  }

  useEffect(() => {
    if (latitude && longitude && searchLevel) {
      setZonePolygons([])
      loadPolygons()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude, searchLevel, locations, drawingTool.isActive])
}
